import { useEffect, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import PrintLogoHeader from "@/components/PrintLogoHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Printer, ClipboardCheck, CheckCircle2, AlertTriangle, XCircle, RotateCcw, Send } from "lucide-react";
import api from "@/lib/api";

// Multi-Point Inspection — 9 systems, ~32 items. Advisor circles or taps
// green (pass), yellow (attention soon), or red (immediate action).
const MPI_SYSTEMS = [
  { name: "Braking", items: ["Front pads", "Rear pads", "Front rotors", "Rear rotors", "Parking brake"] },
  { name: "Tires", items: ["Left front tread", "Right front tread", "Left rear tread", "Right rear tread", "Tire pressure (all 4)"] },
  { name: "Fluids", items: ["Engine oil level & color", "Coolant", "Brake fluid", "Power steering", "Transmission", "Washer fluid"] },
  { name: "Belts & Hoses", items: ["Serpentine belt", "Timing belt / chain", "Radiator hoses", "Heater hoses"] },
  { name: "Battery & Electrical", items: ["Battery voltage & load", "Terminals & cables", "Alternator output"] },
  { name: "Lights", items: ["Headlights (hi/low)", "Tail lights", "Brake lights", "Turn signals", "License plate"] },
  { name: "Wipers & Glass", items: ["Front wipers", "Rear wiper", "Windshield condition"] },
  { name: "Suspension & Steering", items: ["Struts / shocks", "Ball joints", "Tie rod ends", "CV boots"] },
  { name: "Exhaust", items: ["Muffler", "Catalytic converter", "Exhaust pipes & clamps"] },
];

const COLOR_META = {
  green: { label: "Pass", Icon: CheckCircle2, ring: "ring-emerald-500", bg: "bg-emerald-500", text: "text-emerald-700", light: "bg-emerald-50" },
  yellow: { label: "Attention", Icon: AlertTriangle, ring: "ring-amber-500", bg: "bg-amber-500", text: "text-amber-700", light: "bg-amber-50" },
  red: { label: "Immediate", Icon: XCircle, ring: "ring-red-500", bg: "bg-red-500", text: "text-red-700", light: "bg-red-50" },
};

function BlankPrintForm({ customerName, vehicle, ro, advisor }) {
  return (
    <div className="bg-white rounded-lg print:rounded-none border border-neutral-300 p-6 print:p-4 print:border-0" data-testid="mpi-print-form">
      <div className="grid grid-cols-4 gap-3 pb-4 border-b-2 border-navy mb-4 text-xs">
        <div><div className="font-mono-label text-neutral-500">Customer</div><div className="font-bold border-b border-neutral-400 min-h-[1.2rem] pt-0.5">{customerName || "\u00A0"}</div></div>
        <div><div className="font-mono-label text-neutral-500">Vehicle</div><div className="font-bold border-b border-neutral-400 min-h-[1.2rem] pt-0.5">{vehicle || "\u00A0"}</div></div>
        <div><div className="font-mono-label text-neutral-500">RO #</div><div className="font-bold border-b border-neutral-400 min-h-[1.2rem] pt-0.5">{ro || "\u00A0"}</div></div>
        <div><div className="font-mono-label text-neutral-500">Advisor</div><div className="font-bold border-b border-neutral-400 min-h-[1.2rem] pt-0.5">{advisor || "\u00A0"}</div></div>
      </div>
      <div className="grid md:grid-cols-2 gap-x-6 gap-y-4">
        {MPI_SYSTEMS.map((sys) => (
          <div key={sys.name}>
            <div className="font-display font-bold text-sm border-b border-neutral-200 pb-1 mb-2 text-navy">{sys.name}</div>
            {sys.items.map((item) => (
              <div key={item} className="flex items-center justify-between py-1 text-xs">
                <div className="text-neutral-800">{item}</div>
                <div className="flex gap-1.5">
                  <div className="w-4 h-4 rounded-full border-2 border-emerald-500" title="Pass" />
                  <div className="w-4 h-4 rounded-full border-2 border-amber-500" title="Attention" />
                  <div className="w-4 h-4 rounded-full border-2 border-red-500" title="Immediate" />
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="mt-6 pt-4 border-t border-neutral-300 text-[0.65rem] text-neutral-600 flex items-center justify-between print:flex">
        <div>
          Circle green (pass), yellow (attention soon — call before next visit), or red (immediate action recommended). Any red or yellow items get texted to the customer via Decline Drive.
        </div>
        <div className="text-right text-[0.55rem] text-neutral-500 shrink-0 ml-4">
          © {new Date().getFullYear()} Decline Drive™<br />
          Ayers Enterprises
        </div>
      </div>
    </div>
  );
}

function DigitalMPI({ customerName, customerPhone, setCustomerPhone, vehicle, ro, advisor, technicianId }) {
  const [state, setState] = useState({});
  const [handingOff, setHandingOff] = useState(false);
  const setColor = (key, color) => setState((s) => ({ ...s, [key]: s[key] === color ? undefined : color }));
  const reset = () => setState({});
  const counts = { green: 0, yellow: 0, red: 0 };
  Object.values(state).forEach((c) => { if (c) counts[c] = (counts[c] || 0) + 1; });
  const totalItems = MPI_SYSTEMS.reduce((n, s) => n + s.items.length, 0);
  const done = counts.green + counts.yellow + counts.red;
  const followupCount = counts.yellow + counts.red;

  const handoffToTextQueue = async () => {
    if (!customerName || !customerPhone || !vehicle) {
      toast.error("Fill customer name, phone, and vehicle before texting");
      return;
    }
    if (followupCount === 0) {
      toast.error("No yellow or red items to text — nothing to follow up on");
      return;
    }
    const items = [];
    MPI_SYSTEMS.forEach((sys) => {
      sys.items.forEach((item) => {
        const key = `${sys.name}::${item}`;
        const sev = state[key];
        if (sev === "yellow" || sev === "red") {
          items.push({ system: sys.name, item, severity: sev });
        }
      });
    });
    setHandingOff(true);
    try {
      const { data } = await api.post("/mpi/handoff", {
        customer_name: customerName,
        customer_phone: customerPhone,
        vehicle,
        ro: ro || null,
        technician_id: technicianId || null,
        items,
      });
      toast.success(`${data.items} item${data.items === 1 ? "" : "s"} added to text queue. ${customerName.split(" ")[0]} gets the auto-text on the scheduled day.`);
      reset();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Handoff failed");
    } finally {
      setHandingOff(false);
    }
  };

  return (
    <div data-testid="mpi-digital">
      {/* Summary strip */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        {(["green", "yellow", "red"]).map((c) => {
          const m = COLOR_META[c];
          return (
            <Card key={c} className={`p-3 border-l-4 ${m.ring} border-l-${c === "green" ? "emerald" : c === "yellow" ? "amber" : "red"}-500 ${m.light}`}>
              <div className="flex items-center gap-2">
                <m.Icon className={`w-4 h-4 ${m.text}`} strokeWidth={2.5} />
                <div className="font-mono-label text-neutral-600 text-[0.6rem]">{m.label}</div>
              </div>
              <div className="font-display font-black text-3xl tabular-nums mt-1">{counts[c]}</div>
            </Card>
          );
        })}
        <Card className="p-3 border-l-4 border-l-navy bg-muted/40">
          <div className="font-mono-label text-neutral-600 text-[0.6rem]">Progress</div>
          <div className="font-display font-black text-3xl tabular-nums mt-1">
            {done}<span className="text-lg text-muted-foreground">/{totalItems}</span>
          </div>
        </Card>
      </div>

      <div className="mb-3 flex items-center justify-between gap-2 no-print">
        <Button
          onClick={handoffToTextQueue}
          disabled={handingOff || followupCount === 0}
          className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-md"
          data-testid="mpi-handoff-btn"
        >
          <Send className="w-4 h-4 mr-2" strokeWidth={2.5} />
          Text {followupCount} item{followupCount === 1 ? "" : "s"} to customer
        </Button>
        <Button variant="outline" size="sm" onClick={reset} data-testid="mpi-reset">
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} />
          Reset all
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-x-5 gap-y-4">
        {MPI_SYSTEMS.map((sys) => (
          <Card key={sys.name} className="p-4 border-border">
            <div className="font-display font-bold text-sm text-navy border-b border-border pb-1.5 mb-2">
              {sys.name}
            </div>
            {sys.items.map((item) => {
              const key = `${sys.name}::${item}`;
              const chosen = state[key];
              return (
                <div key={item} className="flex items-center justify-between py-1.5 text-sm gap-2">
                  <div className="flex-1 min-w-0 truncate">{item}</div>
                  <div className="flex gap-1 shrink-0">
                    {(["green", "yellow", "red"]).map((c) => {
                      const m = COLOR_META[c];
                      const active = chosen === c;
                      return (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setColor(key, c)}
                          data-testid={`mpi-${sys.name.toLowerCase().replace(/\s+/g, "-")}-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${c}`}
                          className={`w-7 h-7 rounded-full transition-all grid place-items-center ${
                            active ? `${m.bg} ring-2 ring-offset-1 ring-offset-background ${m.ring} scale-110` : "bg-neutral-200 hover:bg-neutral-300"
                          }`}
                          title={m.label}
                        >
                          {active && <m.Icon className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </Card>
        ))}
      </div>
    </div>
  );
}

export default function MPIPage() {
  const [tab, setTab] = useState("digital");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [ro, setRO] = useState("");
  const [advisor, setAdvisor] = useState("");
  const [technicianId, setTechnicianId] = useState("");
  const [technicians, setTechnicians] = useState([]);

  useEffect(() => {
    api.get("/technicians").then((r) => setTechnicians(r.data)).catch(() => {});
  }, []);

  return (
    <DashboardShell>
      <PrintLogoHeader docTitle="Multi-Point Inspection" />

      <div className="mb-6 flex items-start justify-between gap-4 no-print">
        <div>
          <div className="font-mono-label text-muted-foreground">Advisor Tool · Handout</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">
            MPI Inspection
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Every service visit gets a colored inspection sheet. Green = pass. Yellow = talk to them before next visit. Red = immediate action. Yellow + red items become tomorrow's declined-service texts.
          </p>
        </div>
        <Button onClick={() => window.print()} data-testid="print-mpi-btn">
          <Printer className="w-4 h-4 mr-2" strokeWidth={1.75} />
          Print
        </Button>
      </div>

      {/* Tab switch */}
      <div className="flex gap-2 mb-4 no-print">
        <Button
          size="sm"
          variant={tab === "digital" ? "default" : "outline"}
          onClick={() => setTab("digital")}
          data-testid="mpi-tab-digital"
        >
          <ClipboardCheck className="w-4 h-4 mr-2" strokeWidth={2} />
          Digital (iPad)
        </Button>
        <Button
          size="sm"
          variant={tab === "blank" ? "default" : "outline"}
          onClick={() => setTab("blank")}
          data-testid="mpi-tab-blank"
        >
          <Printer className="w-4 h-4 mr-2" strokeWidth={2} />
          Blank Handout
        </Button>
      </div>

      {/* Customer info form (shared) */}
      <Card className="p-4 mb-4 border-border no-print">
        <div className="grid md:grid-cols-6 gap-3">
          <div><Label className="font-mono-label text-[0.6rem]">Customer</Label><Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} className="mt-1 h-9" data-testid="mpi-customer-input" placeholder="Alicia Bennett" /></div>
          <div><Label className="font-mono-label text-[0.6rem]">Phone</Label><Input value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} className="mt-1 h-9" data-testid="mpi-phone-input" placeholder="(913) 555-0123" /></div>
          <div><Label className="font-mono-label text-[0.6rem]">Vehicle</Label><Input value={vehicle} onChange={(e) => setVehicle(e.target.value)} className="mt-1 h-9" data-testid="mpi-vehicle-input" placeholder="2021 Honda CR-V" /></div>
          <div><Label className="font-mono-label text-[0.6rem]">RO #</Label><Input value={ro} onChange={(e) => setRO(e.target.value)} className="mt-1 h-9" data-testid="mpi-ro-input" placeholder="A18374" /></div>
          <div><Label className="font-mono-label text-[0.6rem]">Advisor</Label><Input value={advisor} onChange={(e) => setAdvisor(e.target.value)} className="mt-1 h-9" data-testid="mpi-advisor-input" placeholder="Jayne" /></div>
          <div>
            <Label className="font-mono-label text-[0.6rem]">Technician</Label>
            <select
              value={technicianId}
              onChange={(e) => setTechnicianId(e.target.value)}
              className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              data-testid="mpi-tech-select"
            >
              <option value="">—</option>
              {technicians.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {tab === "digital" ? (
        <DigitalMPI customerName={customerName} customerPhone={customerPhone} setCustomerPhone={setCustomerPhone} vehicle={vehicle} ro={ro} advisor={advisor} technicianId={technicianId} />
      ) : (
        <BlankPrintForm customerName={customerName} vehicle={vehicle} ro={ro} advisor={advisor} />
      )}
    </DashboardShell>
  );
}
